import { memo, useCallback, useRef, useState, type JSX } from 'react'

import { VideoBlob } from '@nativewaves/nw-cloud-sdk/content'
import { useQueryClient } from '@tanstack/react-query'
import { Table as ReactTableType } from '@tanstack/react-table'
import { VirtualItem } from '@tanstack/react-virtual'
import { pick } from 'lodash-es'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { Table } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import {
  useColumns,
  NewDialog,
  RowActions,
  useAsyncContentCaching,
  SuccessPanelActions,
} from 'domains/Workspaces/Video/Objects/Object/Blobs'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { videoObjectBlobsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Objects/Object/Blobs'
import { videoObjectBlobWatchRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Objects/Object/Blobs/Blob'
import { videoObjectQueryKeys } from 'services/queryKeys'

const OverviewRaw = (): JSX.Element => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const routeParams = videoObjectBlobsOverviewRoute.useParams()

  const tableInstance = useRef<ReactTableType<VideoBlob>>(undefined)
  const virtualizerInstanceRef = useRef<VirtualItem[]>(undefined)

  const [newDialogOpen, setNewDialogOpen] = useState(false)
  const [loadingRows, setLoadingRows] = useState<string[]>([])

  const { workspaceQuery, videoBlobsQuery, setBatchGetEnabled } = useAsyncContentCaching({
    virtualItems: virtualizerInstanceRef.current,
    setLoadingRows,
  })

  const columns = useColumns()

  const handleSuccess = useCallback(
    (data: VideoBlob) => {
      enqueueSnackbar(`${t('domain:Workspace.Video.blobCreated', { name: data.storeId })}`, {
        description: `${t('domain:Workspace.Video.blobCreatedDescription')}`,
        variant: 'panel',
        persist: true,
        icon: 'success',
        Actions: (props) => <SuccessPanelActions {...props} {...routeParams} />,
      })
    },
    [enqueueSnackbar, routeParams, t],
  )

  return (
    <>
      <ActionStrip>
        <BasicListOperation
          refreshAction={useCallback(() => {
            queryClient.cancelQueries({ queryKey: videoObjectQueryKeys.blobBatchGets() })
            setBatchGetEnabled(false)
            queryClient.invalidateQueries({
              queryKey: videoObjectQueryKeys.blobList({
                videoId: routeParams.videoobjectId,
              }).queryKey,
            })
          }, [queryClient, setBatchGetEnabled, routeParams.videoobjectId])}
          createAction={useCallback(() => setNewDialogOpen(true), [])}
        />
      </ActionStrip>

      <Table
        getTableInstance={(instance) => (tableInstance.current = instance)}
        size={48}
        data={videoBlobsQuery.data?.flat ?? []}
        columns={columns}
        isInitialLoadingGlobal={workspaceQuery.isLoading || videoBlobsQuery.isLoading}
        isInitialLoadingByRowByID={loadingRows.reduce((acc, row) => ({ ...acc, [row]: [] }), {})}
        enableInfinity
        infinityProps={pick(videoBlobsQuery, ['hasNextPage', 'isFetchingNextPage', 'fetchNextPage'])}
        enableVirtualization
        virtualizedProps={{
          getVirtualItems: (instance) => (virtualizerInstanceRef.current = instance),
        }}
        ActionMenuProps={(row) => ({
          title: row.original.storeId ?? '',
          entity: t('entity:content.blob', { count: 1 }),
        })}
        RowActions={(props) => <RowActions {...props} />}
        tableOptions={{
          getRowId: (video) => video.id,
        }}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={videoObjectBlobsOverviewRoute.fullPath}
            to={videoObjectBlobWatchRoute.to}
            params={{ blobId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
      />
      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)}>
        <NewDialog onSuccess={handleSuccess} />
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
